import React, { useState, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ProfileForm = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    role: '',
    email: '',
    accountId: '',
    emailVerified: false
  });
  const [editData, setEditData] = useState({ ...profileData });
  const fileInputRef = useRef(null);

  const medicalRoles = [
    { value: 'hair-transplant-surgeon', label: 'Hair Transplant Surgeon' },
    { value: 'dermatologist', label: 'Dermatologist' },
    { value: 'plastic-surgeon', label: 'Plastic Surgeon' },
    { value: 'cosmetic-surgeon', label: 'Cosmetic Surgeon' },
    { value: 'medical-technician', label: 'Medical Technician' },
    { value: 'clinic-administrator', label: 'Clinic Administrator' }
  ];

  const handleInputChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setProfileData({ ...editData });
      setIsEditing(false);
      // Success notification would go here
    } catch (error) {
      console.error('Failed to update profile:', error);
      // Error notification would go here
    }
  };

  const handleCancel = () => {
    setEditData({ ...profileData });
    setIsEditing(false);
  };

  const handlePhotoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Simulate upload
      await new Promise(resolve => setTimeout(resolve, 2000));
      // Handle successful upload
    } catch (error) {
      console.error('Failed to upload photo:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileUpload = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div className="p-6">
      {/* Profile Photo Section */}
      <div className="flex flex-col items-center mb-8 pb-6 border-b border-white/10">
        <div className="relative mb-4">
          <div className="w-24 h-24 bg-blue-500/20 rounded-full flex items-center justify-center border-2 border-white/15">
            {isUploading ? (
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
            ) : (
              <Icon name="User" size={32} color="#60A5FA" />
            )}
          </div>
          {isEditing && (
            <button
              onClick={triggerFileUpload}
              disabled={isUploading}
              className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-500 transition-colors disabled:opacity-50"
            >
              <Icon name="Camera" size={14} color="white" />
            </button>
          )}
        </div>

        <div className="text-center mb-4">
          <h3 className="font-medium text-white text-lg">{profileData?.fullName || 'Your Name'}</h3>
          <p className="text-slate-300">{profileData?.role || 'Your Role'}</p>
        </div>

        {isEditing && (
          <button
            onClick={triggerFileUpload}
            disabled={isUploading}
            className="px-4 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition mb-2"
          >
            {isUploading ? 'Uploading...' : 'Change Photo'}
          </button>
        )}

        <p className="text-xs text-slate-400 text-center max-w-xs">
          Professional headshot recommended. Maximum file size 5MB.
          Supported formats: JPG, PNG, GIF.
        </p>
      </div>

      {/* Profile Form */}
      <div className="space-y-5">
        {/* Full Name */}
        <div>
          {isEditing ? (
            <Input
              label="Full Name"
              type="text"
              value={editData?.fullName}
              onChange={(e) => handleInputChange('fullName', e?.target?.value)}
              placeholder="Enter your full name"
              required
            />
          ) : (
            <div>
              <label className="text-sm font-medium text-white/60">Full Name</label>
              <p className="mt-1 text-white">{profileData?.fullName || 'Not provided'}</p>
            </div>
          )}
        </div>

        {/* Role/Title */}
        <div>
          {isEditing ? (
            <Select
              label="Role/Title"
              value={editData?.role}
              onChange={(value) => handleInputChange('role', value)}
              options={medicalRoles}
              placeholder="Select your role"
            />
          ) : (
            <div>
              <label className="text-sm font-medium text-white/60">Role/Title</label>
              <p className="mt-1 text-white">{profileData?.role || 'Not provided'}</p>
            </div>
          )}
        </div>

        {/* Email Address */}
        <div>
          {isEditing ? (
            <Input
              label="Email Address"
              type="email"
              value={editData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              placeholder="Enter your email address"
              required
            />
          ) : (
            <div>
              <label className="text-sm font-medium text-white/60">Email Address</label>
              <div className="mt-1 flex items-center space-x-2">
                <p className="text-white">{profileData?.email || 'Not provided'}</p>
                {profileData?.emailVerified && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300 border border-green-400/20">
                    <Icon name="Check" size={12} className="mr-1" />
                    Verified
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Account ID (Read-only) */}
        <div>
          <label className="text-sm font-medium text-white/60">Account ID</label>
          <p className="mt-1 text-slate-300 font-mono text-sm">{profileData?.accountId || 'Not assigned'}</p>
          <p className="text-xs text-slate-400 mt-1">This ID is used for account identification and cannot be changed.</p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-5 border-t border-white/10">
          {!isEditing ? (
            <div className="flex justify-end w-full">
              <button
                onClick={() => setIsEditing(true)}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition"
              >
                Edit Profile
              </button>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <button
                onClick={handleCancel}
                className="px-5 py-2 rounded-xl bg-white/10 border border-white/20 text-white text-sm hover:bg-white/20 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition"
              >
                Save Changes
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/*"
        className="hidden"
      />
    </div>
  );
};

export default ProfileForm;